import { EnrollmentTaxFilingStatusType, UpdateProfileStatus, useCustomerDetails, useUpdateSuitability } from '@egw/api'
import { Button, Field, ModalDrawer, RadioGroup, useModalState } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { Controller, useForm } from 'react-hook-form'
import { Logger } from '@egw/logger'
import { type FC, useEffect, useState } from 'react'
import { InformationCircleIcon } from '@heroicons/react/16/solid'
import { SuitabilityField } from '../../types'

const log = new Logger('Tax Filing Status')

interface TaxFilingStatusFormInterface {
  [SuitabilityField.TAX_FILING_STATUS]: EnrollmentTaxFilingStatusType
}

export const EditTaxFilingStatusField: FC = () => {
  const { t } = useTranslation('features-settings')
  const { isOpen, openModal, closeModal } = useModalState()

  const [updateError, setUpdateError] = useState<boolean>(false)

  const { data, refetch, isRefetching } = useCustomerDetails()

  const value = data?.customer.taxFilingStatus

  const { mutate: updateSuitability, isLoading } = useUpdateSuitability({
    onSuccess: (res) => {
      if (res.__typename === 'UpdateProfileError' || res.status === UpdateProfileStatus.FAILED) {
        log.error('Error updating tax filing field', { data })
        setUpdateError(true)
      } else if (res.status === UpdateProfileStatus.COMPLETE) {
        void refetch()
          .catch((error: unknown) => {
            log.error(error)
          })
          .finally(() => {
            setUpdateError(false)
            closeModal()
            reset()
          })
      }
    },
    onError: () => {
      setUpdateError(true)
    },
  })

  const defaultValues = {
    [SuitabilityField.TAX_FILING_STATUS]: value,
  }

  const { handleSubmit, control, reset, formState } = useForm<TaxFilingStatusFormInterface>({
    mode: 'all',
    defaultValues,
  })

  useEffect(() => {
    if (data) {
      reset({
        [SuitabilityField.TAX_FILING_STATUS]: value,
      })
    }
  }, [data, reset, value])

  const onSubmit = (formData: TaxFilingStatusFormInterface): void => {
    setUpdateError(false)
    log.debug('Submitting Form', { formData })
    void updateSuitability(formData)
  }

  return value !== undefined ? (
    <>
      <Field
        valueClassName="font-light"
        className="w-full justify-between border-list mb-0 py-4 px-2"
        onEdit={openModal}
        value={t('financial_labels.tax_filing_status')}
        rightValue={t(`enrollment_tax_filing_status.${value}`)}
      />
      <ModalDrawer title={t('financial_labels.tax_filing_status')} isOpen={isOpen} onClose={closeModal}>
        <form
          id="updateTaxFillingStatusForm"
          className="flex flex-col gap-4 h-full md:h-screen justify-between"
          onSubmit={(e) => {
            e.preventDefault()
            void handleSubmit(onSubmit)()
          }}
        >
          <div className="flex flex-col gap-10">
            <div>
              <p className="mt-4 mb-4 font-semibold">{t('update_tax_filling_status.your_tax_filling_status')}</p>
              <Controller
                name={SuitabilityField.TAX_FILING_STATUS}
                control={control}
                render={({ field }) => (
                  <RadioGroup
                    {...field}
                    value={field.value}
                    options={Object.values(EnrollmentTaxFilingStatusType).map((item) => {
                      return {
                        label: t(`enrollment_tax_filing_status.${item}`),
                        value: item,
                      }
                    })}
                  />
                )}
              />
            </div>
            {updateError ? (
              <div className="flex bg-alertErrorBg p-3 rounded-md">
                <InformationCircleIcon className="w-6 h-6 fill-red-500" />
                <p className="text-sm text-alertErrorText ml-2">{t('common.update_error')} </p>
              </div>
            ) : null}
          </div>
          <Button
            id="updateTaxFillingStatus"
            type="submit"
            isLoading={isLoading || isRefetching}
            isDisabled={!formState.isValid}
            className="mt-4 md:mt-0"
          >
            {t('common.confirm')}
          </Button>
        </form>
      </ModalDrawer>
    </>
  ) : null
}
